import { useEffect, useRef, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { UploadCloud, FileSpreadsheet, Sparkles, Download, PenLine, CheckCircle2, Loader2 } from "lucide-react";

const TEMPLATE_ROWS = [
  ["title", "price", "description", "category", "stock", "image_url", "tags"],
  ["Wireless Earbuds Pro", "49.99", "Noise-cancelling earbuds with 30h battery", "electronics", "25", "https://example.com/earbuds.jpg", "audio, wireless"],
  ["Ceramic Coffee Mug", "12.5", "", "home", "100", "", "kitchen, mug"],
];

export default function BulkUpload() {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [aiOn, setAiOn] = useState(true);
  const [aiImgOn, setAiImgOn] = useState(false);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [jobs, setJobs] = useState([]);

  const loadJobs = useCallback(async () => {
    try {
      const { data } = await api.get("/products/bulk/jobs");
      setJobs(data);
      return data;
    } catch {
      return [];
    }
  }, []);

  useEffect(() => { loadJobs(); }, [loadJobs]);

  useEffect(() => {
    if (!jobs.some((j) => j.ai_remaining > 0 || j.images_remaining > 0)) return;
    const t = setInterval(loadJobs, 6000);
    return () => clearInterval(t);
  }, [jobs, loadJobs]);

  const downloadTemplate = () => {
    const csv = TEMPLATE_ROWS.map((r) => r.map((c) => `"${c}"`).join(",")).join("\n");
    const url = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url;
    a.download = "vismarks-catalog-template.csv";
    a.click();
    URL.revokeObjectURL(url);
  };

  const upload = async () => {
    if (!file) return toast.error("Choose a .csv or .xlsx file first");
    setBusy(true);
    setResult(null);
    try {
      const form = new FormData();
      form.append("file", file);
      form.append("ai_descriptions", aiOn);
      form.append("ai_images", aiImgOn);
      const { data } = await api.post("/products/bulk/upload", form, {
        headers: { "Content-Type": "multipart/form-data" },
        timeout: 120000,
      });
      setResult(data);
      toast.success(data.message);
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
      loadJobs();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto px-6 lg:px-10 py-10" data-testid="bulk-upload-page">
      <div className="text-xs uppercase tracking-widest text-[#007AFF]">Catalog import</div>
      <h1 className="mt-2 font-black tracking-tight text-4xl">Upload your whole database</h1>
      <p className="mt-3 text-neutral-400 max-w-2xl">
        Two ways to list — pick whichever fits the moment. Add products one at a time, or import
        thousands in one go from a CSV / Excel export of your inventory system.
      </p>

      <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Link to="/seller/new" data-testid="manual-upload-option" className="rounded-xl border border-[#27272A] bg-[#121212] p-6 hover:border-white/30 transition-colors group">
          <PenLine className="h-5 w-5 text-neutral-400 group-hover:text-white transition-colors" />
          <div className="mt-3 font-bold text-lg">Manual upload</div>
          <p className="mt-1 text-sm text-neutral-400">One product at a time with photos and the Gemini listing assistant. Best for hero items.</p>
        </Link>
        <div data-testid="database-upload-option" className="rounded-xl border border-[#007AFF]/50 bg-[#007AFF]/[0.06] p-6">
          <FileSpreadsheet className="h-5 w-5 text-[#007AFF]" />
          <div className="mt-3 font-bold text-lg">Database upload</div>
          <p className="mt-1 text-sm text-neutral-400">Import a .csv or .xlsx export — up to 20,000 rows per file. AI writes missing descriptions automatically.</p>
        </div>
      </div>

      <div className="mt-8 rounded-xl border border-[#27272A] bg-[#121212] p-6">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="font-bold text-lg">Import file</div>
          <button onClick={downloadTemplate} data-testid="download-template-btn" className="inline-flex items-center gap-1.5 text-xs text-[#007AFF] hover:underline">
            <Download className="h-3.5 w-3.5" /> Download CSV template
          </button>
        </div>
        <p className="mt-2 text-xs text-neutral-500">
          Required columns: <span className="font-mono text-neutral-300">title, price</span>. Optional:{" "}
          <span className="font-mono text-neutral-300">description, category, stock, image_url, tags</span>.
          Common column names (name, qty, photo…) are auto-detected.
        </p>

        <label
          htmlFor="bulk-file-input"
          className="mt-5 flex flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-[#3f3f46] hover:border-[#007AFF]/60 transition-colors p-10 cursor-pointer"
        >
          <UploadCloud className="h-8 w-8 text-neutral-500" />
          <span className="text-sm text-neutral-300 font-semibold">{file ? file.name : "Click to choose your .csv or .xlsx file"}</span>
          <span className="text-xs text-neutral-500">Max 20MB · 20,000 rows per upload</span>
          <input
            id="bulk-file-input"
            ref={fileRef}
            type="file"
            accept=".csv,.xlsx,.xls"
            data-testid="bulk-file-input"
            className="hidden"
            onChange={(e) => setFile(e.target.files?.[0] || null)}
          />
        </label>

        <label className="mt-4 flex items-start gap-3 cursor-pointer rounded-md border border-[#27272A] bg-[#0A0A0A] p-3.5">
          <input
            type="checkbox"
            checked={aiOn}
            onChange={(e) => setAiOn(e.target.checked)}
            data-testid="bulk-ai-checkbox"
            className="mt-0.5 h-4 w-4 accent-[#007AFF]"
          />
          <span className="text-xs text-neutral-400 leading-relaxed">
            <span className="text-white font-semibold inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[#FF9F0A]" /> AI descriptions</span>{" "}
            — Gemini writes a compelling description and search tags for every item missing one, in the background.
            You can adjust any of them afterwards from your dashboard.
          </span>
        </label>

        <label className="mt-3 flex items-start gap-3 cursor-pointer rounded-md border border-[#27272A] bg-[#0A0A0A] p-3.5">
          <input
            type="checkbox"
            checked={aiImgOn}
            onChange={(e) => setAiImgOn(e.target.checked)}
            data-testid="bulk-ai-images-checkbox"
            className="mt-0.5 h-4 w-4 accent-[#007AFF]"
          />
          <span className="text-xs text-neutral-400 leading-relaxed">
            <span className="text-white font-semibold inline-flex items-center gap-1"><Sparkles className="h-3.5 w-3.5 text-[#007AFF]" /> AI product photos</span>{" "}
            — generate a studio product photo for rows without an image (first {""}
            <span className="text-white">25 items per upload</span>, uses AI credits). You can also
            generate photos one-by-one later from the edit dialog.
          </span>
        </label>

        <Button
          onClick={upload}
          disabled={busy || !file}
          data-testid="bulk-upload-btn"
          className="mt-5 w-full h-12 rounded-full bg-[#007AFF] hover:bg-[#0066d6] text-white font-bold"
        >
          {busy ? (<><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Importing…</>) : (<><UploadCloud className="h-4 w-4 mr-2" /> Import products</>)}
        </Button>

        {result && (
          <div data-testid="bulk-result" className="mt-5 rounded-lg border border-[#34C759]/40 bg-[#34C759]/[0.06] p-4">
            <div className="flex items-center gap-2 text-[#34C759] font-bold">
              <CheckCircle2 className="h-4 w-4" /> {result.created.toLocaleString()} products live now
            </div>
            <div className="mt-1 text-xs text-neutral-400">
              {result.skipped > 0 && <>Skipped {result.skipped} invalid rows. </>}
              {result.ai_pending > 0 && <>AI is writing {result.ai_pending.toLocaleString()} descriptions in the background. </>}
              {result.image_pending > 0 && <>AI is creating {result.image_pending.toLocaleString()} product photos.</>}
            </div>
            {result.errors?.length > 0 && (
              <ul className="mt-2 text-[11px] text-neutral-500 space-y-0.5">
                {result.errors.map((er, i) => (<li key={i}>Row {er.row}: {er.reason}</li>))}
              </ul>
            )}
          </div>
        )}
      </div>

      {jobs.length > 0 && (
        <div className="mt-8">
          <h2 className="font-bold text-lg">Import history</h2>
          <div className="mt-3 rounded-xl border border-[#27272A] overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-[#0A0A0A] text-xs uppercase tracking-widest text-neutral-500">
                <tr>
                  <th className="text-left px-4 py-3">File</th>
                  <th className="text-right px-4 py-3">Created</th>
                  <th className="text-right px-4 py-3">Skipped</th>
                  <th className="text-right px-4 py-3">AI descriptions</th>
                  <th className="text-right px-4 py-3">AI photos</th>
                </tr>
              </thead>
              <tbody>
                {jobs.map((j) => (
                  <tr key={j.job_id} data-testid="bulk-job-row" className="border-t border-[#27272A]">
                    <td className="px-4 py-3 text-neutral-300">{j.filename}</td>
                    <td className="px-4 py-3 text-right font-mono">{j.created.toLocaleString()}</td>
                    <td className="px-4 py-3 text-right font-mono text-neutral-500">{j.skipped}</td>
                    <td className="px-4 py-3 text-right text-xs">
                      {j.ai_remaining > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-[#FF9F0A]"><Loader2 className="h-3 w-3 animate-spin" /> {j.ai_remaining.toLocaleString()} remaining</span>
                      ) : j.ai_pending_at_upload > 0 ? (
                        <span className="text-[#34C759]">✓ {j.ai_pending_at_upload.toLocaleString()} written</span>
                      ) : (
                        <span className="text-neutral-600">—</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-xs">
                      {j.images_remaining > 0 ? (
                        <span className="inline-flex items-center gap-1.5 text-[#007AFF]"><Loader2 className="h-3 w-3 animate-spin" /> {j.images_remaining.toLocaleString()} remaining</span>
                      ) : j.images_pending_at_upload > 0 ? (
                        <span className="text-[#34C759]">✓ {j.images_pending_at_upload.toLocaleString()} created</span>
                      ) : (
                        <span className="text-neutral-600">—</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
